import { useId } from "react";
import {
	Bar,
	BarChart,
	CartesianGrid,
	Legend,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	XAxisTickContentProps,
	YAxis,
} from "recharts";
import { formatCount, Series, seriesOrder } from "../dashboardConfig";
import ChartTooltip from "./ChartTooltip";

/** One bar: its axis label, its full name, and a count per series key. */
export type StackedBarDatum = {
	id: string;
	label: string;
	fullLabel: string;
} & Record<string, string | number>;

interface StackedBarChartProps {
	data: StackedBarDatum[];
	series: Series[];
	/** An extra tooltip line for one segment, e.g. the master tenant's share. */
	segmentDetail?: (
		datum: StackedBarDatum,
		seriesKey: string
	) => string | undefined;
	/** Text alternative for screen readers. */
	summary: string;
}

/** An axis label that shows the full name on hover. */
function CategoryTick({
	x,
	y,
	label,
	fullLabel,
}: {
	x: number | string;
	y: number | string;
	label: string;
	fullLabel?: string;
}) {
	return (
		<text
			x={x}
			y={y}
			dy={12}
			textAnchor="middle"
			className="recharts-cartesian-axis-tick-value"
		>
			{fullLabel && <title>{fullLabel}</title>}
			{label}
		</text>
	);
}

/** Vertical bars stacked by series, e.g. report types by status. */
function StackedBarChart({
	data,
	series,
	segmentDetail,
	summary,
}: StackedBarChartProps) {
	const captionId = useId();
	return (
		<figure className="dashboard-chart">
			<figcaption id={captionId} className="sr-only">
				{summary}
			</figcaption>
			<div className="dashboard-chart__plot">
				<ResponsiveContainer width="100%" height="100%" minHeight={120}>
					<BarChart
						// Names the keyboard-focusable chart surface.
						aria-labelledby={captionId}
						data={data}
						margin={{ top: 4, right: 4, bottom: 0, left: 0 }}
					>
						<CartesianGrid vertical={false} />
						<XAxis
							dataKey="label"
							interval={0}
							tickLine={false}
							tick={({ x, y, payload, index }: XAxisTickContentProps) => (
								<CategoryTick
									x={x}
									y={y}
									label={String(payload.value)}
									fullLabel={data[index]?.fullLabel}
								/>
							)}
						/>
						<YAxis
							allowDecimals={false}
							tickCount={3}
							width={32}
							axisLine={false}
							tickLine={false}
						/>
						<Legend
							position="top"
							iconType="square"
							iconSize={9}
							itemSorter={seriesOrder(series)}
						/>
						<Tooltip
							shared={false}
							content={({ active, payload }) => {
								const entry = payload?.[0];
								const item = series.find((s) => s.key === entry?.dataKey);
								if (!active || !entry || !item) return null;
								const datum = entry.payload as StackedBarDatum;
								const detail = segmentDetail?.(datum, item.key);
								return (
									<ChartTooltip
										rows={[
											{
												color: item.color,
												text: `${datum.fullLabel} · ${item.label} | ${formatCount(Number(entry.value))}`,
											},
										]}
										details={detail ? [detail] : []}
									/>
								);
							}}
						/>
						{series.map((item) => (
							<Bar
								key={item.key}
								dataKey={item.key}
								name={item.label}
								stackId="total"
								fill={item.color}
								maxBarSize={36}
							/>
						))}
					</BarChart>
				</ResponsiveContainer>
			</div>
		</figure>
	);
}

export default StackedBarChart;
